import type { InferGetStaticPropsType, NextPage } from "next";
import Image from "next/image";
import {
  CMSContent,
  ExternalLink,
  HeroSplashImage,
  InternalLink,
  Line,
  P,
  YoutubePlayer,
} from "@sorcery/ui";
import { getSanityImageUrl } from "@sorcery/utils/client/sanity";
import { Page } from "~/components/layouts/Page";
import { FeatureTitle } from "~/components/layouts/titles/FeatureTitle";
import { ArtistBioContent } from "~/components/molecules/cms/artist/ArtistBioContent";
import { MorePosts } from "~/components/molecules/cms/post/MorePosts";
import { env } from "~/env";
import NotFound from "~/pages/404/404";
import { artistPath, newsItemPath } from "~/utils/paths";
import type { getStaticProps } from "./index.page";

const Artist: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = (props) => {
  // available instantly due to ssr helpers
  const { data, features } = props;

  // Redirect if not found
  if (!data) {
    return <NotFound />;
  }

  return (
    <Page
      title={data.name}
      description={"Sorcery: Contested Realm | Contributing Artist"}
      image={getSanityImageUrl(
        data.hero,
        env.NEXT_PUBLIC_SANITY_PROJECT_ID,
        env.NEXT_PUBLIC_SANITY_DATASET,
      )
        .width(1200)
        .height(630)
        .url()}
      hero={
        <HeroSplashImage
          src={getSanityImageUrl(
            data.hero,
            env.NEXT_PUBLIC_SANITY_PROJECT_ID,
            env.NEXT_PUBLIC_SANITY_DATASET,
          )
            .width(2000)
            .height(1800)
            .url()}
          blurSrc={getSanityImageUrl(
            data.hero,
            env.NEXT_PUBLIC_SANITY_PROJECT_ID,
            env.NEXT_PUBLIC_SANITY_DATASET,
          )
            .width(24)
            .height(24)
            .blur(10)
            .url()}
        />
      }
    >
      <FeatureTitle
        title={data.name}
        subtitle={
          <>
            {/* With Sorcery since {format(new Date(data.joinedAt))} */}
            {data.link && (
              <>
                {/* <span>&nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;</span> */}
                <ExternalLink color={"primary"} href={data.link}>
                  Support {data.name.split(" ")[0]}
                </ExternalLink>
              </>
            )}
          </>
        }
      />

      {/* profile */}
      <section
        className={
          "sorcery-section sorcery-container !my-4 flow-root !py-4 duration-1000 animate-in fade-in"
        }
      >
        <div className={"mx-auto mb-4 sm:float-left sm:mr-8 sm:w-2/5"}>
          <Image
            width={500}
            height={600}
            alt={data.name}
            src={getSanityImageUrl(
              data.profile,
              env.NEXT_PUBLIC_SANITY_PROJECT_ID,
              env.NEXT_PUBLIC_SANITY_DATASET,
            )
              .width(500)
              .height(600)
              .url()}
          />
        </div>

        <ArtistBioContent content={data.bio} />
      </section>

      {/* content */}
      <section className={"sorcery-section sorcery-container !my-4 !py-4"}>
        <Line className="mb-12" />
        <div className="flow-root">
          <CMSContent
            content={data.content}
            config={{
              projectId: env.NEXT_PUBLIC_SANITY_PROJECT_ID,
              dataset: env.NEXT_PUBLIC_SANITY_DATASET,
            }}
            marks={{
              post: ({ value, children }) => {
                //
                const { slug } = value;
                const href = newsItemPath(slug as string);
                return (
                  <InternalLink href={href} target="_blank" color="primary">
                    {children}
                  </InternalLink>
                );
              },
              artist: ({ value, children }) => {
                //
                const { slug } = value;
                const href = artistPath(slug as string);
                return (
                  <InternalLink href={href} target="_blank" color="primary">
                    {children}
                  </InternalLink>
                );
              },
            }}
          />
        </div>
      </section>

      <section className={"sorcery-section sorcery-container !my-4 !py-4"}>
        {data.featureVideo && (
          <>
            <Line className="my-8" />
            <YoutubePlayer url={data.featureVideo.url} />
          </>
        )}
      </section>

      {data.link && (
        <section className={"sorcery-section sorcery-container !my-4 !py-4"}>
          <Line className="my-8" />

          <P>
            <span>Find more of&nbsp;</span>
            {data.name.split(" ")[0]}
            <span>'s work @</span>{" "}
            <ExternalLink color={"primary"} href={data.link}>
              {data.link}
            </ExternalLink>
          </P>
        </section>
      )}

      {features?.length > 0 && (
        <section className="sorcery-container sorcery-section">
          <MorePosts posts={features} header="Artist Features" />
        </section>
      )}
    </Page>
  );
};

export default Artist;
